import { buildBackendUrl } from './backendUrl';
import { buildMedhugRequestHeaders } from '../utils/requestHeaders';

async function parseJsonResponse(response) {
  try {
    return await response.json();
  } catch (error) {
    return {};
  }
}

function buildError(data, fallback) {
  const error = new Error(data.message || data.error || fallback);
  error.code = data.error;
  error.data = data;
  return error;
}

export async function getGlp1BridgeAccessStatus() {
  const response = await fetch(buildBackendUrl('/api/glp1-bridge/access/status'), {
    method: 'GET',
    headers: buildMedhugRequestHeaders(),
    credentials: 'include',
  });

  const data = await parseJsonResponse(response);
  if (!response.ok) {
    throw buildError(data, 'Unable to check provider access right now.');
  }
  return data;
}

export async function requestGlp1BridgeAccessCode({ email }) {
  const response = await fetch(buildBackendUrl('/api/glp1-bridge/access/request-code'), {
    method: 'POST',
    headers: buildMedhugRequestHeaders({
      'Content-Type': 'application/json',
    }),
    credentials: 'include',
    body: JSON.stringify({ email }),
  });

  const data = await parseJsonResponse(response);
  if (!response.ok) {
    throw buildError(data, 'Unable to send an access code right now.');
  }
  return data;
}

export async function verifyGlp1BridgeAccessCode({ email, code }) {
  const response = await fetch(buildBackendUrl('/api/glp1-bridge/access/verify-code'), {
    method: 'POST',
    headers: buildMedhugRequestHeaders({
      'Content-Type': 'application/json',
    }),
    credentials: 'include',
    body: JSON.stringify({ email, code }),
  });

  const data = await parseJsonResponse(response);
  if (!response.ok) {
    throw buildError(data, 'Unable to verify the access code right now.');
  }
  return data;
}

export async function logoutGlp1BridgeAccess() {
  const response = await fetch(buildBackendUrl('/api/glp1-bridge/access/logout'), {
    method: 'POST',
    headers: buildMedhugRequestHeaders({
      'Content-Type': 'application/json',
    }),
    credentials: 'include',
    body: JSON.stringify({}),
  });

  const data = await parseJsonResponse(response);
  if (!response.ok) {
    throw buildError(data, 'Unable to sign out right now.');
  }
  return data;
}

export async function recordGlp1BridgeScreenerEvent({ eventName, payload = {} }) {
  const response = await fetch(buildBackendUrl('/api/glp1-bridge/screener-event'), {
    method: 'POST',
    headers: buildMedhugRequestHeaders({
      'Content-Type': 'application/json',
    }),
    credentials: 'include',
    body: JSON.stringify({
      event_name: eventName,
      payload: payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {},
    }),
  });

  const data = await parseJsonResponse(response);
  if (!response.ok) {
    throw buildError(data, 'Unable to record screener telemetry right now.');
  }
  return data;
}

// v2 non-streaming endpoint. Use as fallback when streaming is unavailable
// (e.g., the network blocks SSE, or the caller is doing a batch eval).
// Returns the same v2 response shape the streaming endpoint settles on.
export async function askGlp1BridgeChatV2({ question, conversation = [], screening, screeningContext, signal }) {
  const response = await fetch(buildBackendUrl('/api/glp1-bridge/chat/v2'), {
    method: 'POST',
    headers: buildMedhugRequestHeaders({
      'Content-Type': 'application/json',
    }),
    credentials: 'include',
    body: JSON.stringify({ question, conversation, screening, screening_context: screeningContext }),
    signal,
  });

  const data = await parseJsonResponse(response);

  if (!response.ok && !data.answer) {
    throw buildError(data, 'Unable to answer this question right now.');
  }

  return {
    ...data,
    ok: response.ok,
    status: response.status,
  };
}

// Legacy v1 alias kept so any existing callers that still reach for
// askGlp1BridgeChat() don't break. New code should call askGlp1BridgeChatV2
// or streamGlp1BridgeChat.
export const askGlp1BridgeChat = askGlp1BridgeChatV2;

// v2 streaming endpoint. Returns an AbortController-aware promise that
// resolves when the stream closes. Caller provides an `onEvent(name, data)`
// callback that fires for each parsed SSE event:
//
//   - "meta"               : { engine_version, request_id, answer_model, grounding_model }
//   - "answer_stream_start": Anthropic accepted; the model is generating
//   - "text_delta"         : { key: "direct_answer"|"body_markdown", delta: "..." }
//   - "answer_complete"    : raw tool arguments
//   - "answer_usage"       : Anthropic token usage for the answer call
//   - "citations_enriched" : { citations: [{ source_id, locator, supporting_quote, source_title, audience, authority, url }] }
//   - "answer_ready"       : answer is ready to render; grounding may follow
//   - "grounding_start"    : grounding audit kicked off
//   - "grounding"          : { is_grounded, unsupported_claims, notes, final_confidence }
//   - "done"               : stream is closing; total latency, warnings
//   - "screener_complete"  : when the payload was a screener submission
//   - "log"                : { chat_log_id, request_id }
//   - "error"              : { error: "...", detail: "..." }
//   - "phi_blocked"        : { answer, detected_identifier_types }
//
// SSE parsing follows the standard "event: <name>\ndata: <json>\n\n" format.
// We use fetch+ReadableStream rather than EventSource so we can POST.
export async function streamGlp1BridgeChat({
  question,
  conversation = [],
  screening,
  screeningContext,
  onEvent,
  signal,
}) {
  const response = await fetch(buildBackendUrl('/api/glp1-bridge/chat/stream'), {
    method: 'POST',
    headers: buildMedhugRequestHeaders({
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    }),
    credentials: 'include',
    body: JSON.stringify({ question, conversation, screening, screening_context: screeningContext }),
    signal,
  });

  if (!response.ok) {
    let data = {};
    try { data = await response.json(); } catch (e) { /* ignore */ }
    throw buildError(data, 'Unable to start the answer stream right now.');
  }

  if (!response.body || typeof response.body.getReader !== 'function') {
    throw new Error('Streaming is not supported by this browser.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let currentEvent = null;
  let dataLines = [];

  const dispatch = () => {
    if (!currentEvent && dataLines.length === 0) return;
    const dataStr = dataLines.join('\n');
    let payload = {};
    try { payload = dataStr ? JSON.parse(dataStr) : {}; } catch (err) { payload = { _raw: dataStr }; }
    if (currentEvent && onEvent) {
      try { onEvent(currentEvent, payload); } catch (err) { /* caller errors shouldn't kill the stream */ }
    }
    currentEvent = null;
    dataLines = [];
  };

  try {
    // Read until the server closes or the caller aborts.
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // SSE events are separated by blank lines; each event has one or more
      // "event:" / "data:" lines. We split on newline and dispatch on each
      // blank line.
      let nlIdx;
      while ((nlIdx = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, nlIdx).replace(/\r$/, '');
        buffer = buffer.slice(nlIdx + 1);
        if (line === '') {
          dispatch();
        } else if (line.startsWith('event:')) {
          currentEvent = line.slice('event:'.length).trim();
        } else if (line.startsWith('data:')) {
          dataLines.push(line.slice('data:'.length).replace(/^ /, ''));
        }
        // ignore comments (lines starting with ':') and unknown fields
      }
    }
    // Flush any trailing event when the stream closes without a blank-line terminator.
    if (currentEvent || dataLines.length > 0) {
      dispatch();
    }
  } catch (err) {
    if (err && err.name === 'AbortError') {
      // Caller cancelled; let it bubble for the caller to handle.
      throw err;
    }
    throw err;
  } finally {
    try { reader.cancel(); } catch (e) { /* ignore */ }
  }
}

export async function submitGlp1BridgeFeedback({ chatLogId, requestId, feedbackType }) {
  const response = await fetch(buildBackendUrl('/api/glp1-bridge/feedback'), {
    method: 'POST',
    headers: buildMedhugRequestHeaders({
      'Content-Type': 'application/json',
    }),
    credentials: 'include',
    body: JSON.stringify({
      chat_log_id: chatLogId,
      request_id: requestId,
      feedback_type: feedbackType,
    }),
  });

  const data = await parseJsonResponse(response);

  if (!response.ok) {
    throw buildError(data, 'Unable to record feedback right now.');
  }

  return data;
}
